import _ from 'lodash'

import { filterKeys } from '../../configuration/filtering'

function formatDestionationsContent(destinations) {
  return _.reduce(destinations, (destinationMap, item) => ({
    ...destinationMap,
    [item.id]: item,
  }), {})
}

function constructDestinationsFromItinerary(itinerary, fallbackDestinations, destinationsContent) {
  if (_.isEmpty(itinerary)) {
    return _.map(fallbackDestinations, item => ({
      id: item,
      title: _.get(destinationsContent, `[${item}].name`)
    }))
  }
  return _.reduce(itinerary, (destinations, item) => {
    const updatedDestinations = _.cloneDeep(destinations)
    _.forEach(_.get(item, 'destinations', []), destinationItem => {
      if (_.get(destinationItem, 'destinationId') !== _.get(_.last(updatedDestinations), 'id')) {
        updatedDestinations.push({
          id: _.get(destinationItem, 'destinationId'),
          title: _.get(destinationsContent, `[${_.get(destinationItem, 'destinationId')}].name`)
        })
      }
    })
    return updatedDestinations
  }, [])
}

function constructRegionsFromTripDestinations(destinationIds, destinations) {
  return _.uniq(_.flatMap(destinationIds, id => _.get(destinations, `[${id}].region`)))
}

function formatTourDestinations(destinations) {
  const destinationList = _.map(destinations, destination => _.get(destination, 'title'))
  return _.filter(destinationList, destination => destination)
}

function getDestinationIdList(destinations) {
  const destinationList = _.map(destinations, destination => _.get(destination, 'id'))
  return _.filter(destinationList, destination => destination)
}

/**
 * An offer is an active flash sale if its isFlashSale flag is true,
 * it has a status value of true, and it meets the trip duration
 * requirements.
 */
function getActiveFlashSaleOffer(offers, numberOfNightsInTrip) {
  return _.find(offers, offer => {
    const isFlashSale = _.get(offer, 'flashSale')
    const offerStatus = _.get(offer, 'status')
    const requiredDuration = _.get(offer, 'requiredDuration')
      ? _.parseInt(_.get(offer, 'requiredDuration'))
      : 0
    return offerStatus && isFlashSale && numberOfNightsInTrip >= requiredDuration
  })
}

/**
 * We assume that if an offer description contains numeric digits, has a status
 * value of true, and meets the trip duration requirements, it is valid. Offers
 * are filtered based on that assumption, then all non-numeric characters are
 * stripped from the descriptions and the offers are sorted in descending order
 * by the discount amount.
 */
function getHighestDiscountOffer(offers, numberOfNightsInTrip) {
  const offersWithValidDiscounts = _.filter(offers,
    offer => {
      const requiredDuration = _.get(offer, 'requiredDuration')
        ? _.parseInt(_.get(offer, 'requiredDuration'))
        : 0
      return /\d/.test(_.get(offer, 'priceDescriptionPrimary'))
        && _.get(offer, 'status')
        && numberOfNightsInTrip >= requiredDuration
    }
  )
  const offersWithFormattedDiscounts = _.map(offersWithValidDiscounts, offer => {
    const offerDescription = _.get(offer, 'priceDescriptionPrimary')
    const discountValue = parseInt(offerDescription.replace(/\D/g,''), 10);
    return {
      ...offer,
      discountValue,
    }
  })
  const offersSortedByDiscountValue = _.orderBy(offersWithFormattedDiscounts, ['discountValue'], ['desc'])
  return offersSortedByDiscountValue[0]
}

/**
 * The priority offer is either the active flash sale offer
 * or the active offer with the highest discount.
 */
function getPriorityOffer(offers, tripDuration) {
  // The softrip duration is determined by the number of days in the trip, while the
  // offer duration is based on the number of nights. We subtract 1 from the duration
  // in order to determine whether the trip fits the offer duration.
  const numberOfNightsInTrip = tripDuration - 1
  const flashSaleOffer = getActiveFlashSaleOffer(offers, numberOfNightsInTrip)
  let priorityOffer = flashSaleOffer
  if (!flashSaleOffer) {
    priorityOffer = getHighestDiscountOffer(offers, numberOfNightsInTrip)
  }
  return _.get(priorityOffer, 'flag') || _.get(priorityOffer, 'priceDescriptionPrimary')
  }

/**
 * The Most Popular vacation type is only used as an override to bump
 * tours to the top of the Most Popular list. We take the 50 most
 * popular tours less the number of tours that include the override
 * and add the Most Popular flag to their vacation type taxonomy
*/
function getToursToAddAsMostPopular(tours) {
  const minNumberOfMostPopularTours = 50;
  let numberOfToursWithMostPopularVacationType = 0;
  let toursWithoutMostPopularVacationType = [];

  _.forEach(tours, (tour) => {
    const tourVacationTypes = _.map(_.get(tour, 'viewExtensions.vacationTypes', []), item => item.title);
    const hasMostPopularVacationType = _.some(tourVacationTypes, item => _.includes(item, 'Most Popular'));
    if (hasMostPopularVacationType) {
      numberOfToursWithMostPopularVacationType = numberOfToursWithMostPopularVacationType + 1;
    } else {
      toursWithoutMostPopularVacationType.push(tour);
    }
  });

  const toursWithoutMostPopularVacationTypeSortedByPopularity = _.sortBy(toursWithoutMostPopularVacationType, (tour) => _.get(tour, 'viewExtensions.popularity'));
  const numberOfToursToAddToMostPopular = Math.max(minNumberOfMostPopularTours - numberOfToursWithMostPopularVacationType, 0);
  const toursToAddAsMostPopular = _.take(toursWithoutMostPopularVacationTypeSortedByPopularity, numberOfToursToAddToMostPopular)
  return _.map(toursToAddAsMostPopular, tour => tour.id)
}

export function formatTours(tours, destinations) {
  const destinationsContent = formatDestionationsContent(destinations)
  const viewableTours = _.filter(tours, tour => !!_.get(tour, 'viewExtensions.published'))

  const toursToAddAsMostPopular = getToursToAddAsMostPopular(viewableTours)

  return _.reduce(viewableTours, (toursMap, tour) => {
    const tourId = _.get(tour, 'id')

    const fallbackDestinations = _.get(tour, 'viewExtensions.locationIds', [])
    const destinations = constructDestinationsFromItinerary(
      _.get(tour, 'viewExtensions.itinerary', []),
      fallbackDestinations,
      destinationsContent,
    )
    const regions = constructRegionsFromTripDestinations(
      _.get(tour, 'viewExtensions.locationIds', []),
      destinationsContent,
    )
    const duration = _.get(tour, 'viewExtensions.duration')
    let popularity = _.get(tour, 'viewExtensions.popularity')
    const vacationTypes = _.map(_.get(tour, 'viewExtensions.vacationTypes', []), item => _.replace(item.title, 'TripType_', ''))

    // if vacation types already includes Most Popular, override popularity value
    if (_.includes(vacationTypes, 'Most Popular')) {
      popularity = 0
    }
    if (_.includes(toursToAddAsMostPopular, tourId)) {
      vacationTypes.push('Most Popular')
    }

    return {
      ...toursMap,
      [`${tour.id}`]: {
        id: _.get(tour, 'id'),
        advertisedPrice: _.get(tour, 'viewExtensions.advertisedPrice'),
        [`${filterKeys.countries}`]: _.get(tour, 'viewExtensions.countryNames', []),
        [`${filterKeys.destinationCount}`]: _.size(_.get(tour, 'viewExtensions.destinations', [])),
        [`${filterKeys.destinationEnd}`]: _.last(destinations) ? [ _.last(destinations).id] : [],
        [`${filterKeys.destinationIncludes}`]: getDestinationIdList(destinations),
        [`${filterKeys.destinationStart}`]: _.first(destinations) ? [ _.first(destinations).id] : [],
        displayDestinations: formatTourDestinations(destinations),
        [`${filterKeys.duration}`]: duration,
        [`${filterKeys.famousRoutes}`]: _.map(_.get(tour, 'viewExtensions.famousRoutes', []), item => _.replace(item, 'Route_', '')),
        image: _.get(tour, 'viewExtensions.images.image.path')
          || _.get(tour, 'viewExtensions.images.heroImage.path')
          || _.get(tour, 'viewExtensions.images.image')
          || _.get(tour, 'viewExtensions.images.detailsImage.path'),
        map: _.get(tour, 'viewExtensions.images.mapImage.path')
          || _.get(tour, 'viewExtensions.images.mapImage'),
        [`${filterKeys.nationalParks}`]: _.get(tour, 'viewExtensions.nationalParks', []),
        offer: getPriorityOffer(_.get(tour, 'viewExtensions.offers', []), duration),
        popularity,
        [`${filterKeys.regions}`]: regions,
        startDate: _.get(tour, 'viewExtensions.createdAt'),
        title: _.get(tour, 'basicInfo.name'),
        trains: _.map(_.get(tour, 'viewExtensions.relatedTrains'), train => _.replace(_.get(train, 'trainTitle'), 'Train_', '')),
        tripTag:  _.get(tour, 'viewExtensions.tripTag.title') && _.get(tour, 'viewExtensions.tripTag'),
        url: _.get(tour, 'viewExtensions.url'),
        [`${filterKeys.vacationTypes}`]: vacationTypes,
      }
    }
  }, {})
}
